import { Dispatch, GetState } from "common/types";

import { getClient } from "services";
import errorHandler from "services/memoryService/errorHandler";

import { analyticsLogWinMemory } from "seneca-common/features/memories/analytics";
import { isSignedIn } from "seneca-common/features/user/state";

import {
  createMemoryActionFactory,
  fetchAllMemoriesActionFactory,
  getMemories,
  memoriesNeedFetching,
  MemoryType,
  receiveMemories,
  reconcileMemoriesLocally,
  saveMemoryLocally
} from "../state";

const getMemoryServiceClient = () => getClient("memoryService");

export const fetchMemories = fetchAllMemoriesActionFactory({
  requestFunction: () => getMemoryServiceClient().fetchAllMemories(),
  storeAction: receiveMemories,
  errorHandler
});

export const fetchMemoriesIfNotFetched =
  () => async (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const signedIn = isSignedIn(state);

    if (signedIn && memoriesNeedFetching(state)) {
      return dispatch(fetchMemories());
    }
  };

const clientSaveMemoryFunction = createMemoryActionFactory({
  requestFunction: memory => getMemoryServiceClient().createMemory(memory),
  storeAction: saveMemoryLocally,
  errorHandler
});

export const saveNewMemory = (memory: MemoryType) => {
  return async function (dispatch: Dispatch, getState: GetState) {
    const state = getState();
    const signedIn = isSignedIn(state);
    // Save locally first so user doesn't see lag from api request
    // @ts-ignore
    dispatch(saveMemoryLocally(memory));
    analyticsLogWinMemory(memory, state);

    if (signedIn) {
      await dispatch(clientSaveMemoryFunction(memory));
    }
  };
};

// Logs fetch meta data but doesn't store memories to store
const fetchMemoriesForReconciliation = fetchAllMemoriesActionFactory({
  requestFunction: () => getMemoryServiceClient().fetchAllMemories(),
  errorHandler
});

export const reconcileMemories = () => {
  return async function (dispatch: Dispatch, getState: GetState) {
    const state = getState();

    // Cache local memories
    const localMemories = getMemories(state);

    // Fetch memories and check if the user had any stored from previous sessions
    const persistedMemories = await dispatch(fetchMemoriesForReconciliation());

    // Only allow user to keep memories earned when anonymous if they don't
    // have any memories stored on their user
    if (persistedMemories && persistedMemories.length > 0) {
      return dispatch(reconcileMemoriesLocally(persistedMemories));
    }

    if (localMemories && localMemories.size > 0) {
      localMemories.forEach(memory => {
        let memoryToStore = memory.toJS();
        delete memoryToStore.id;

        dispatch(clientSaveMemoryFunction(memoryToStore));
      });
    }
  };
};
